import { useState, useMemo } from "react";
import { useLocation, useNavigate, Outlet } from "react-router-dom";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Input } from "@/components/ui/input";
import { cn } from "@/lib/utils";
import { MobileBottomNav } from "@/components/mobile";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

import {
  LayoutDashboard,
  Users,
  Wallet,
  BarChart3,
  ShieldAlert,
  Coins,
  LineChart,
  Settings,
  Bell,
  Search,
  LogOut,
  User,
  ChevronDown,
  Shield,
  FileText,
  MessageSquare,
  Activity,
  ChevronLeft,
  ChevronRight,
  ArrowLeft,
  ArrowLeftRight,
  Network,
} from "lucide-react";
import { logOut } from "@/store/actions/authActions";
import {
  useDisputes,
  useKycSubmissions,
  useSystemNotifications,
  useTickets,
  useUserSearch,
} from "@/hooks";
import { useAppDispatch, useAppSelector } from "@/store/hooks";
import ScrollToTop from "../ScrollToTop";
import SmoothScroll from "../SmoothScroll";
import { format } from "timeago.js";
import type { Notification } from "@/interfaces/notification";
import { SupportStatus } from "@/interfaces/ticket";
import { getErrorMessage } from "@/utils/getErrorMessage";
import { queryClient } from "@/services/queryClient";
import { makeRequest } from "@/utils/makeRequest";
import { KycStatus } from "@/interfaces/kyc";
import { DisputeStatus } from "@/interfaces/dispute";
import { Logo } from "../Logo";

// Quick stats for header
const quickStats = [
  { label: "Active Users", value: "24.5K", change: "+12%" },
  { label: "Daily Volume", value: "$2.4M", change: "+8%" },
  { label: "Pending KYC", value: "156", change: "-5%" },
];

export default function AdminLayout() {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useAppDispatch();
  const { user } = useAppSelector((state) => state.auth);
  const userId = user?.id ?? "";

  const [isCollapsed, setIsCollapsed] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [showSearchResults, setShowSearchResults] = useState(false);

  const { data: kycResponse } = useKycSubmissions();

  const pendingKycCount = useMemo(() => {
    return (
      kycResponse?.data?.filter((kyc) => kyc.status !== KycStatus.APPROVED)
        .length || 0
    );
  }, [kycResponse]);

  const [page, setPage] = useState(1);
  const limit = 5;
  const { data: notificationData } = useSystemNotifications({ page, limit });
  const { data: disputes } = useDisputes({ page, limit });

  const unreadNotifications = notificationData?.data?.filter(
    (notif: Notification) =>
      notif?.recipients?.some((r) => r.userId === userId && !r.isRead),
  );

  console.log("NOTIFS ADM", notificationData);

  const unresolvedDisputes = disputes?.data.filter(
    (dispute) => dispute.status !== DisputeStatus.RESOLVED,
  ).length;

  const { data: tickets } = useTickets();
  const openTicketSCount = tickets?.data?.filter(
    (ticket) => ticket.status == SupportStatus.OPEN,
  ).length;

  const { data: searchResults = [], isFetching } = useUserSearch(searchQuery);

  const handleLogout = () => {
    dispatch(logOut());
    toast.success("Logged out", {
      description: "You have been successfully logged out.",
    });
    navigate("/login");
  };

  const handleSearchSelect = (item: any) => {
    setSearchQuery("");
    setShowSearchResults(false);

    if (item.id) {
      navigate(`/admin/users`);
    }
  };

  const handleNotificationClick = async (notif: Notification) => {
    try {
      const res = await makeRequest.put(`/notification/${notif.id}/read`);

      if (res) {
        queryClient.invalidateQueries({
          queryKey: ["notifications-system"],
        });

        // navigate("/admin/compliance");
        navigate(notif?.meta?.endpoint!);
      }
    } catch (error) {
      toast.error(getErrorMessage(error));
      console.log(error);
    }
  };

  const isActive = (path: string) => {
    if (path === "/admin") {
      return location.pathname === "/admin";
    }
    return location.pathname.startsWith(path);
  };

  // Check if we're on a sub-page (not the main admin dashboard)
  const isSubPage = location.pathname !== "/admin";

  // Get page title based on current route
  const getPageTitle = () => {
    const path = location.pathname;
    if (path === "/admin") return "Admin Dashboard";
    if (path.includes("/users")) return "Users";
    if (path.includes("/wallets")) return "Wallets";
    if (path.includes("/transactions")) return "Transactions";
    if (path.includes("/notifications")) return "Notifications";
    if (path.includes("/transaction-tier-settings")) return "Tier Settings";
    if (path.includes("/trades")) return "Trades";
    if (path.includes("/disputes")) return "Disputes";
    if (path.includes("/tokens")) return "Tokens";
    if (path.includes("/networks")) return "Blockchain Neworks";
    if (path.includes("/analytics")) return "Analytics";
    if (path.includes("/compliance")) return "KYC";
    if (path.includes("/reports")) return "Reports";
    if (path.includes("/support")) return "Support";
    if (path.includes("/logs")) return "Logs";
    if (path.includes("/settings")) return "Settings";
    return "Admin";
  };

  // Admin navigation items
  const adminNavItems = [
    { title: "Dashboard", path: "/admin", icon: LayoutDashboard },
    { title: "Analytics", path: "/admin/analytics", icon: LineChart },
    { title: "Users", path: "/admin/users", icon: Users },
    { title: "Wallets", path: "/admin/wallets", icon: Wallet },
    {
      title: "Transactions",
      path: "/admin/transactions",
      icon: ArrowLeftRight,
    },
    {
      title: "Notifications",
      path: "/admin/notifications",
      icon: Bell,
      badge: unreadNotifications?.length,
    },
    { title: "Trades", path: "/admin/trades", icon: BarChart3 },
    {
      title: "Disputes",
      path: "/admin/disputes",
      icon: ShieldAlert,
      badge: unresolvedDisputes,
    },
    { title: "Tokens", path: "/admin/tokens", icon: Coins },
    { title: "Blockchain Networks", path: "/admin/networks", icon: Network },
    {
      title: "KYC",
      path: "/admin/compliance",
      icon: Shield,
      badge: pendingKycCount.toString(),
    },
    { title: "Reports", path: "/admin/reports", icon: FileText },
    {
      title: "Support",
      path: "/admin/support",
      icon: MessageSquare,
      badge: openTicketSCount,
    },
    { title: "Logs", path: "/admin/logs", icon: Activity },
    { title: "Settings", path: "/admin/settings", icon: Settings },
    {
      title: "Tier Settings",
      path: "/admin/transaction-tier-settings",
      icon: Settings,
    },
  ];

  return (
    <TooltipProvider delayDuration={100}>
      <div className="flex min-h-screen w-full bg-background">
        <ScrollToTop />
        <SmoothScroll />
        {/* Desktop Sidebar - Hidden on mobile */}
        <aside
          className={cn(
            "fixed left-0 top-0 h-screen z-40 bg-sidebar border-r border-sidebar-border flex-col transition-all duration-300 ease-in-out hidden lg:flex",
            isCollapsed ? "w-[70px]" : "w-[260px]",
          )}
        >
          {/* Sidebar Header */}
          <div className="h-16 border-b border-sidebar-border flex items-center px-3 shrink-0">
            <div
              className="flex items-center gap-3 cursor-pointer"
              onClick={() => navigate("/admin")}
            >
              {isCollapsed && (
                <div className="flex h-10 w-10 items-center justify-center rounded-xl shrink-0">
                  <Logo
                    iconOnly
                    size="xl"
                    className="transition-transform duration-300"
                  />
                </div>
              )}
              {!isCollapsed && (
                <div className="flex flex-col">
                  <Logo
                    size="xl"
                    className="transition-transform duration-300"
                  />

                  {/* <span className="text-xs text-sidebar-foreground/60">
                    Admin Portal
                  </span> */}
                </div>
              )}
            </div>
          </div>

          {/* Sidebar Navigation - Scrollable */}
          <nav
            data-lenis-prevent
            className="flex-1 overflow-y-auto overflow-x-hidden py-4 px-2"
          >
            <ul className="space-y-1">
              {adminNavItems.map((item) => {
                const active = isActive(item.path);

                return (
                  <li key={item.path}>
                    {isCollapsed ? (
                      <Tooltip delayDuration={100}>
                        <TooltipTrigger asChild>
                          <button
                            onClick={() => navigate(item.path)}
                            className={cn(
                              "w-full flex items-center justify-center px-3 py-2.5 rounded-lg transition-all duration-200 relative",
                              active
                                ? "bg-primary/20 text-primary"
                                : "text-sidebar-foreground/70 hover:bg-sidebar-accent hover:text-sidebar-foreground",
                            )}
                          >
                            <item.icon
                              className={cn(
                                "h-5 w-5",
                                active ? "text-primary" : "",
                              )}
                            />
                            {active && (
                              <div className="absolute left-0 top-1/2 -translate-y-1/2 w-1 h-6 bg-primary rounded-r-full" />
                            )}
                          </button>
                        </TooltipTrigger>
                        <TooltipContent side="right" sideOffset={10}>
                          <div className="flex items-center gap-2">
                            {item.title}
                            {item.badge && (
                              <Badge className="h-4 min-w-4 px-1 text-[10px]">
                                {item.badge}
                              </Badge>
                            )}
                          </div>
                        </TooltipContent>
                      </Tooltip>
                    ) : (
                      <button
                        onClick={() => navigate(item.path)}
                        className={cn(
                          "w-full flex items-center gap-3 px-3 py-2.5 rounded-lg transition-all duration-200 relative",
                          active
                            ? "bg-primary/20 text-primary"
                            : "text-sidebar-foreground/70 hover:bg-sidebar-accent hover:text-sidebar-foreground",
                        )}
                      >
                        <item.icon
                          className={cn(
                            "h-5 w-5 shrink-0",
                            active ? "text-primary" : "",
                          )}
                        />
                        <span className="font-medium text-sm flex-1 text-left">
                          {item.title}
                        </span>
                        {item.badge && (
                          <Badge className="h-5 min-w-5 px-1.5 text-[10px] bg-primary/20 text-primary border-0">
                            {item.badge}
                          </Badge>
                        )}
                        {active && (
                          <div className="absolute left-0 top-1/2 -translate-y-1/2 w-1 h-6 bg-primary rounded-r-full" />
                        )}
                      </button>
                    )}
                  </li>
                );
              })}
            </ul>
          </nav>

          {/* Sidebar Footer */}
          <div className="border-t border-sidebar-border p-3 shrink-0">
            {!isCollapsed && (
              <div className="rounded-lg bg-sidebar-accent/50 p-3 mb-3">
                <div className="flex items-center gap-2 mb-2">
                  <div className="h-2 w-2 rounded-full bg-emerald-500 animate-pulse" />
                  <span className="text-xs text-sidebar-foreground/70">
                    System Status
                  </span>
                </div>
                <div className="flex items-center justify-between text-xs">
                  <span className="text-emerald-500">Operational</span>
                  <span className="text-sidebar-foreground/50">99.9%</span>
                </div>
              </div>
            )}

            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button className="w-full flex items-center gap-2 px-2 py-2 rounded-lg hover:bg-sidebar-accent transition-colors">
                  <Avatar className="h-8 w-8 border-2 border-primary/30 shrink-0">
                    <AvatarImage src={user?.avatar} />
                    <AvatarFallback className="bg-primary/20 text-primary text-xs">
                      {user?.firstName?.charAt(0).toUpperCase() || "A"}
                    </AvatarFallback>
                  </Avatar>
                  {!isCollapsed && (
                    <>
                      <div className="flex flex-col items-start flex-1 min-w-0">
                        <span className="text-sm font-medium truncate">
                          {user?.firstName + " " + user?.lastName || "Admin"}
                        </span>
                        <span className="text-xs text-sidebar-foreground/60 truncate">
                          {user?.email || "admin@buyfiat.app"}
                        </span>
                      </div>
                      <ChevronDown className="h-4 w-4 text-sidebar-foreground/50 shrink-0" />
                    </>
                  )}
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56">
                <DropdownMenuLabel>My Account</DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={() => navigate("/admin/settings")}>
                  <User className="mr-2 h-4 w-4" />
                  Profile
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => navigate("/admin/settings")}>
                  <Settings className="mr-2 h-4 w-4" />
                  Settings
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem
                  onClick={handleLogout}
                  className="text-red-500"
                >
                  <LogOut className="mr-2 h-4 w-4" />
                  Logout
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </aside>

        {/* Main Content Area */}
        <div
          className={cn(
            "flex-1 flex flex-col min-w-0 transition-all duration-300 ease-in-out",
            "lg:ml-0",
            isCollapsed ? "lg:ml-[70px]" : "lg:ml-[260px]",
          )}
        >
          {/* Mobile Header - Clean with back button */}
          <header className="lg:hidden fixed top-0 left-0 right-0 z-30 h-14 bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60 border-b flex items-center justify-between px-0 sm:px-4">
            {/* Left: Back button on sub-pages */}
            <div className="flex items-center gap-3">
              {isSubPage ? (
                <button
                  onClick={() => navigate(-1)}
                  className="flex items-center justify-center w-9 h-9 rounded-full hover:bg-muted transition-colors"
                >
                  <ArrowLeft className="h-6 w-6" />
                </button>
              ) : (
                <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-gradient-to-br from-primary to-primary/80">
                  <Logo
                    iconOnly
                    size="xl"
                    className="transition-transform duration-300"
                  />
                </div>
              )}
              <span className="font-semibold text-base">{getPageTitle()}</span>
            </div>

            {/* Right: Notifications only */}
            <button
              onClick={() => navigate("/admin/support")}
              className="relative flex items-center justify-center w-9 h-9 rounded-full hover:bg-muted transition-colors"
            >
              <Bell className="h-6 w-6" />
              <span className="absolute top-1 right-1 h-2 w-2 rounded-full bg-primary" />
            </button>
          </header>

          {/* Desktop Header */}
          <header
            className={cn(
              "hidden lg:flex fixed top-0 left-0 right-0 z-30 h-16 border-b bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60 items-center justify-between gap-4 px-6 transition-all duration-300 ease-in-out",
              isCollapsed ? "lg:left-[70px]" : "lg:left-[260px]",
            )}
          >
            {/* Left Section */}
            <div className="flex items-center gap-4 flex-1 min-w-0">
              <button
                className="flex p-2 hover:bg-muted rounded-lg shrink-0"
                onClick={() => setIsCollapsed(!isCollapsed)}
              >
                {isCollapsed ? (
                  <ChevronRight className="h-5 w-5" />
                ) : (
                  <ChevronLeft className="h-5 w-5" />
                )}
              </button>

              {/* Search with Results */}
              <div className="flex-1 max-w-md min-w-0 relative">
                <div className="relative w-full">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input
                    placeholder="Search users, transactions..."
                    className="pl-10 w-full bg-muted"
                    value={searchQuery}
                    onChange={(e) => {
                      setSearchQuery(e.target.value);
                      setShowSearchResults(e.target.value.length > 0);
                    }}
                    onFocus={() => setShowSearchResults(searchQuery.length > 0)}
                  />
                </div>

                {/* Search Results Dropdown */}
                {showSearchResults && searchQuery.trim().length >= 3 && (
                  <div className="absolute top-full left-0 right-0 mt-2 bg-card border border-border rounded-lg shadow-lg z-50 max-h-80 overflow-auto">
                    <div className="p-2">
                      <p className="text-xs text-muted-foreground px-2 py-1">
                        Search Results ({searchResults.length})
                      </p>
                      {isFetching && (
                        <div className="p-3 text-xs text-muted-foreground">
                          Searching...
                        </div>
                      )}
                      {searchResults.map((item: any, idx: number) => (
                        <button
                          key={idx}
                          onClick={() => handleSearchSelect(item)}
                          className="w-full flex items-center gap-3 px-3 py-2 hover:bg-muted rounded-lg transition-colors text-left"
                        >
                          <div
                            className={cn(
                              "w-8 h-8 rounded-lg flex items-center justify-center text-xs font-medium",
                              item.type === "user" &&
                                "bg-blue-500/20 text-blue-500",
                              item.type === "transaction" &&
                                "bg-emerald-500/20 text-emerald-500",
                              item.type === "trade" &&
                                "bg-violet-500/20 text-violet-500",
                              item.type === "kyc" &&
                                "bg-amber-500/20 text-amber-500",
                              item.type === "dispute" &&
                                "bg-red-500/20 text-red-500",
                            )}
                          >
                            {item.type === "user"
                              ? "U"
                              : item.type === "transaction"
                                ? "TX"
                                : item.type === "trade"
                                  ? "TR"
                                  : item.type === "kyc"
                                    ? "K"
                                    : "D"}
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium truncate">
                              {item.name || item.title || item.id}
                            </p>
                            <p className="text-xs text-muted-foreground truncate">
                              {item.type === "user"
                                ? item.email
                                : item.type === "transaction"
                                  ? item.user
                                  : item.type}
                            </p>
                          </div>
                          <span className="text-xs text-muted-foreground capitalize">
                            {item.type}
                          </span>
                        </button>
                      ))}
                    </div>
                  </div>
                )}
                {showSearchResults &&
                  searchQuery &&
                  searchResults.length === 0 && (
                    <div className="absolute top-full left-0 right-0 mt-2 bg-card border border-border rounded-lg shadow-lg z-50 p-4 text-center">
                      <p className="text-sm text-muted-foreground">
                        No results found for "{searchQuery}"
                      </p>
                    </div>
                  )}
              </div>
            </div>

            {/* Right Section */}
            <div className="flex items-center gap-2 shrink-0">
              {/* Quick Stats */}
              <div className="hidden xl:flex items-center gap-6 mr-4">
                {quickStats.map((stat) => (
                  <div key={stat.label} className="flex flex-col">
                    <span className="text-[10px] text-muted-foreground uppercase tracking-wider">
                      {stat.label}
                    </span>
                    <div className="flex items-center gap-1.5">
                      <span className="text-sm font-semibold">
                        {stat.value}
                      </span>
                      <span
                        className={cn(
                          "text-[10px]",
                          stat.change.startsWith("+")
                            ? "text-emerald-500"
                            : "text-red-500",
                        )}
                      >
                        {stat.change}
                      </span>
                    </div>
                  </div>
                ))}
              </div>

              {/* Notifications */}
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" size="icon" className="relative">
                    <Bell className="h-5 w-5" />
                    {unreadNotifications && (
                      <span className="absolute -top-1 -right-1 h-4 w-4 rounded-full bg-primary text-[10px] font-medium text-primary-foreground flex items-center justify-center">
                        {unreadNotifications?.length &&
                          unreadNotifications?.length}
                      </span>
                    )}
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-80">
                  <DropdownMenuLabel className="flex items-center justify-between">
                    Notifications
                    <Badge variant="secondary" className="text-[10px]">
                      {unreadNotifications?.length} new
                    </Badge>
                  </DropdownMenuLabel>

                  <DropdownMenuSeparator />

                  {unreadNotifications
                    ?.slice(0, 5)
                    ?.map((notif: Notification) => {
                      return (
                        <DropdownMenuItem
                          key={notif.id}
                          className="flex items-start gap-3 p-3 cursor-pointer"
                          onClick={() => handleNotificationClick(notif)}
                        >
                          <div className="flex-1 min-w-0">
                            <p className="font-medium text-sm">{notif.title}</p>
                            <p className="text-xs text-muted-foreground">
                              {notif.description}
                            </p>
                            <p className="text-xs text-muted-foreground mt-1">
                              {format(notif.createdAt)}
                            </p>
                          </div>
                        </DropdownMenuItem>
                      );
                    })}

                  <DropdownMenuSeparator />

                  <div className="p-2">
                    <DropdownMenuItem
                      onClick={() => navigate("/admin/notifications")}
                      className="justify-center font-medium cursor-pointer"
                    >
                      View All Notifications
                    </DropdownMenuItem>
                  </div>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </header>

          {/* Page Content - with padding for headers */}
          <main className="flex-1 p-1 lg:p-6 overflow-auto pt-16 lg:pt-20 pb-24 lg:pb-6">
            {/* <div className="mx-auto max-w-7xl"> */}
            <Outlet />
            {/* </div> */}
          </main>
        </div>

        {/* Mobile Bottom Navigation */}
        <div className="lg:hidden">
          <MobileBottomNav userType="admin" />
        </div>
      </div>

      {/* Click outside to close search results */}
      {showSearchResults && (
        <div
          className="fixed inset-0 z-20"
          onClick={() => setShowSearchResults(false)}
        />
      )}
    </TooltipProvider>
  );
}
